import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { PricingTable } from '../../shared/types';

interface Platform {
  key: string;
  name: string;
}

interface InstallCodeData {
  platform: string;
  pricingTableId: string;
  pricingTableName: string;
  installCode: string;
  containerId: string;
}

interface InstallCodeResponse {
  success: boolean;
  data: {
    installCode: string;
  };
  message?: string;
}

export function InstallPage() {
  const [pricingTables, setPricingTables] = useState<PricingTable[]>([]);
  const [activeTable, setActiveTable] = useState<PricingTable | null>(null);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('vanilla');
  const [containerId, setContainerId] = useState<string>('pricing-table-container');
  const [installCode, setInstallCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState<string>('');
  const [isGeneratingApiKey, setIsGeneratingApiKey] = useState<boolean>(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  console.log('InstallPage mounted');
  console.log('Current apiKey state:', apiKey);

  // Fetch pricing tables, active table, platforms, and API key on component mount
  useEffect(() => {
    console.log('useEffect triggered');
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        console.log('Fetching pricing tables...');
        
        // Fetch all pricing tables for the user
        const pricingTablesResponse = await fetch('/api/pricing-tables', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        console.log('Pricing tables response status:', pricingTablesResponse.status);

        if (!pricingTablesResponse.ok) {
          const errorText = await pricingTablesResponse.text();
          console.error('Pricing tables error response:', errorText);
          throw new Error(`Failed to load pricing tables: ${pricingTablesResponse.status}`);
        }

        const pricingTablesData = await pricingTablesResponse.json();
        console.log('Pricing tables data:', pricingTablesData);
        setPricingTables(pricingTablesData.data);

        // Find the active table
        const active = pricingTablesData.data.find((table: PricingTable) => table.is_active);
        setActiveTable(active || null);

        console.log('Fetching platforms...');
        
        // Fetch supported platforms
        const platformsResponse = await fetch('/api/install/platforms');
        console.log('Platforms response status:', platformsResponse.status);
        
        if (!platformsResponse.ok) {
          const errorText = await platformsResponse.text();
          console.error('Platforms error response:', errorText);
          throw new Error(`Failed to load platforms: ${platformsResponse.status}`);
        }

        const platformsData = await platformsResponse.json();
        console.log('Platforms data:', platformsData);
        setPlatforms(platformsData.data);

        // Set default platform
        if (platformsData.data.length > 0) {
          setSelectedPlatform(platformsData.data[0].key);
        }

        // Ensure user has API key (generate if missing)
        console.log('Ensuring user has API key...');
        const apiKeyResponse = await fetch('/api/auth/ensure-api-key', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (apiKeyResponse.ok) {
          const apiKeyData = await apiKeyResponse.json();
          console.log('API key response data:', apiKeyData);
          console.log('API key response structure:', {
            success: apiKeyData.success,
            apiKey: apiKeyData.apiKey,
            message: apiKeyData.message,
            hasApiKey: !!apiKeyData.apiKey,
            apiKeyLength: apiKeyData.apiKey?.length
          });
          setApiKey(apiKeyData.apiKey);
          console.log('API key ensured:', apiKeyData.message);
        } else {
          console.error('Failed to ensure API key:', apiKeyResponse.status);
          // Fallback to fetching user data to get existing API key
          const userResponse = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

          if (userResponse.ok) {
            const userData = await userResponse.json();
            console.log('User data response:', userData);
            console.log('User data structure:', {
              success: userData.success,
              data: userData.data,
              user: userData.data?.user,
              apiKey: userData.data?.user?.api_key,
              hasApiKey: !!userData.data?.user?.api_key,
              apiKeyLength: userData.data?.user?.api_key?.length
            });
            if (userData.data?.user?.api_key) {
              setApiKey(userData.data.user.api_key);
              console.log('API key found in user data:', userData.data.user.api_key);
            } else {
              console.log('No API key found in user data');
            }
          } else {
            console.error('Failed to fetch user data:', userResponse.status);
          }
        }
      } catch (error: any) {
        console.error('Error in fetchData:', error);
        setError(error.message || 'Error loading data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Generate installation code when platform, container ID, or active table changes
  useEffect(() => {
    if (selectedPlatform && apiKey && activeTable) {
      generateInstallCode();
    }
  }, [selectedPlatform, containerId, apiKey, activeTable]);

  // Debug useEffect to track API key changes
  useEffect(() => {
    console.log('API key state changed:', apiKey);
  }, [apiKey]);

  const generateInstallCode = async () => {
    if (!activeTable || !selectedPlatform || !apiKey) {
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch('/api/install/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          platform: selectedPlatform,
          containerId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate installation code');
      }

      const data: InstallCodeResponse = await response.json();
      // The backend returns installCode inside data.data.installCode
      const installCode = data.data?.installCode;
      setInstallCode(installCode);
    } catch (error: any) {
      setError(error.message || 'Error generating installation code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setIsGeneratingApiKey(true);
    setError(null);
    
    try {
      const response = await fetch('/api/auth/generate-api-key', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (data.error === 'API_KEY_EXISTS') {
          throw new Error('You already have an API key. Cannot generate a new one.');
        }
        throw new Error(data.message || 'Failed to generate API key');
      }

      // Success - update the API key
      setApiKey(data.apiKey);
      
      // Refresh the page data to get the updated API key
      window.location.reload();
    } catch (error: any) {
      setError(error.message || 'Error generating API key');
    } finally {
      setIsGeneratingApiKey(false);
    }
  };

  /*
    FUTURE: Remove this handleGenerateApiKey function entirely and rely wholly on the two automatic generator checks:
    1. API key generation during user registration (already implemented)
    2. Automatic API key generation when user visits install page (ensure-api-key endpoint)
    
    This will simplify the code and ensure users always have an API key without manual intervention.
  */

  const handleToggleTableStatus = async (tableId: number) => {
    setIsTogglingStatus(true);
    try {
      const response = await fetch(`/api/pricing-tables/${tableId}/toggle-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle table status');
      }

      // Refresh the page to update the UI
      window.location.reload();
    } catch (error: any) {
      setError(error.message || 'Error toggling table status');
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(type);
      console.log(`${type} copied to clipboard!`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Only log the error, don't show browser notification
      console.error('Failed to copy to clipboard. Please select and copy the code manually.');
    }
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
        <p>Failed to load data: {error}</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <h1 className="text-3xl font-bold mb-2">Install Pricing Table</h1>
      <p className="text-gray-600">
        Generate integration code to embed your pricing tables on your website
      </p>

      <div className="grid gap-6 mt-8">
        {/* API Key Management */}
        <Card>
          <CardHeader>
            <CardTitle>API Key</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Your API key is used to authenticate requests to our service. API keys are automatically generated when you create an account and cannot be regenerated.
            </p>
            {apiKey ? (
              <div className="space-y-4">
                <div className="relative">
                  <Input
                    type="text"
                    value={apiKey}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                  <Button
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                    onClick={() => copyToClipboard(apiKey, 'API Key')}
                  >
                    {copiedCode === 'API Key' ? '✓ Copied' : 'Copy'}
                  </Button>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm">
                    ✅ Your API key is ready to use. Copy it above to integrate your pricing table.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">No API key found. Generate one to get started.</p>
                <Button
                  onClick={handleGenerateApiKey}
                  disabled={isGeneratingApiKey}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isGeneratingApiKey ? 'Generating...' : 'Generate API Key'}
                </Button>
                {isGeneratingApiKey && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      🔄 Generating your API key...
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* 
              FUTURE: Remove this generate API key section entirely and rely wholly on the two automatic generator checks:
              1. API key generation during user registration (already implemented)
              2. Automatic API key generation when user visits install page (ensure-api-key endpoint)
              
              This will simplify the UI and ensure users always have an API key without manual intervention.
            */}
          </CardContent>
        </Card>

        {/* Active Table Status */}
        <Card>
          <CardHeader>
            <CardTitle>Active Pricing Table</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Only one pricing table can be active at a time. The active table will be served by the API.
            </p>
            {activeTable ? (
              <div className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-green-50">
                <div>
                  <h3 className="font-semibold text-green-800">{activeTable.name}</h3>
                  <p className="text-green-600 text-sm">Currently active</p>
                </div>
                <Button
                  onClick={() => handleToggleTableStatus(activeTable.id)}
                  disabled={isTogglingStatus}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {isTogglingStatus ? 'Deactivating...' : 'Deactivate'}
                </Button>
              </div>
            ) : (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <p className="text-gray-600">No active pricing table</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Table Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Your Pricing Tables</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Manage your pricing tables and set which one is active</p>
            {pricingTables && pricingTables.length > 0 ? (
              <div className="space-y-3">
                {pricingTables.map((table: PricingTable) => (
                  <div key={table.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                    <div>
                      <h3 className="font-semibold">{table.name}</h3>
                      <p className="text-sm text-gray-600">
                        Created: {new Date(table.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {table.is_active ? (
                        <Badge className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Active</Badge>
                      ) : (
                        <Button
                          onClick={() => handleToggleTableStatus(table.id)}
                          disabled={isTogglingStatus}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {isTogglingStatus ? 'Activating...' : 'Activate'}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No pricing tables found</p>
                <div className="text-sm text-gray-500">
                  Create a pricing table first to get started.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {apiKey && activeTable && (
          <div className="space-y-6">
            {/* Platform Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Select Platform</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">Choose your website's platform for optimized integration code</p>
                <select
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  {platforms.map((platform) => (
                    <option key={platform.key} value={platform.key}>
                      {platform.name}
                    </option>
                  ))}
                </select>
              </CardContent>
            </Card>

            {/* Integration Code */}
            <Card>
              <CardHeader>
                <CardTitle>Integration Code</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Copy and paste this code into your {selectedPlatform} website to display your active pricing table
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                    <code>{installCode || (isGenerating ? 'Generating installation code...' : 'No code generated')}</code>
                  </pre>
                  <Button
                    className="absolute top-2 right-2 px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600"
                    onClick={() => copyToClipboard(installCode, 'Integration Code')}
                  >
                    {copiedCode === 'Integration Code' ? '✓ Copied' : 'Copy'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Documentation */}
            <Card>
              <CardHeader>
                <CardTitle>Integration Guide</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Step-by-step instructions for integrating your pricing table
                </p>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Step 1: Copy the Integration Code</h3>
                    <p className="text-gray-600">
                      Copy the integration code from the "Integration Code" section above.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Step 2: Paste into Your Website</h3>
                    <p className="text-gray-600">
                      Paste the code into your {selectedPlatform} website where you want the pricing table to appear.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Step 3: Test the Integration</h3>
                    <p className="text-gray-600">
                      Load your website and verify that the pricing table appears correctly.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-2">Important Notes:</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>The API will always return your currently active pricing table</li>
                      <li>Only one pricing table can be active at a time</li>
                      <li>You can customize the appearance by modifying the CSS</li>
                      <li>The pricing table will update automatically when you make changes in the dashboard</li>
                      <li>Visitor tracking is automatically included for analytics</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Troubleshooting</h4>
                    <ul className="text-blue-700 space-y-1">
                      <li>• If the pricing table doesn't appear, check your browser's console for errors</li>
                      <li>• Ensure your website allows external JavaScript to run</li>
                      <li>• Verify that the API key is correct and a pricing table is active</li>
                      <li>• Make sure your platform supports the integration method shown</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {(!apiKey || !activeTable) && (
          <Card>
            <CardHeader>
              <CardTitle>Setup Required</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!apiKey && (
                  <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                    <h3 className="font-semibold text-yellow-800 mb-2">API Key Required</h3>
                    <p className="text-yellow-700">Your API key should have been generated automatically when you created your account. If it's missing, you can generate one below.</p>
                  </div>
                )}
                {!activeTable && (
                  <div className="p-4 border border-yellow-200 rounded-lg bg-yellow-50">
                    <h3 className="font-semibold text-yellow-800 mb-2">Active Table Required</h3>
                    <p className="text-yellow-700">You need to activate a pricing table to serve it via the API.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
